import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Modal,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  IconButton,
  Typography,
  Container,
  Grid,
  Paper,
  Toolbar,
  Snackbar,
  Alert,
} from "@mui/material";
import { SelectChangeEvent } from "@mui/material"; // Import SelectChangeEvent
import CloseIcon from "@mui/icons-material/Close";
import { templateService } from "../../apis/templates"; // Assuming templateService is imported
import { Template, MessageType } from "../../types/templates"; // Assuming types are imported
import {
  Editor,
  BtnUndo,
  BtnRedo,
  Separator,
  BtnBold,
  BtnItalic,
  BtnUnderline,
  BtnStrikeThrough,
  BtnNumberedList,
  BtnBulletList,
  BtnLink,
  BtnClearFormatting,
  HtmlButton,
  BtnStyles,
} from "react-simple-wysiwyg";
import { User } from "../../types/users";
import {
  ManualEmailNotification,
  ManualPushNotification,
  ManualSMSNotification,
  ManualWhatsAppNotification,
} from "../../types/notification";
import { notificationService } from "../../apis/manualnotification";

const NotificationPopup: React.FC<{ disabled: boolean; selectedUsers: User[] }> = ({
  disabled,
  selectedUsers,
}) => {
  const [open, setOpen] = useState(false);
  const [selectedMessageType, setSelectedMessageType] = useState<MessageType | "">("");
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [filteredTemplates, setFilteredTemplates] = useState<Template[]>([]);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">("success");

  const fetchTemplates = async () => {
    try {
      const response = await templateService.getTemplates();
      setTemplates(response);
      // Filter templates based on selected message type
      if (selectedMessageType) {
        setFilteredTemplates(response.filter(template => template.type === selectedMessageType));
      } else {
        setFilteredTemplates([]);
      }
    } catch (error) {
      console.error("Error fetching templates:", error);
    }
  };

  useEffect(() => {
    if (open) {
      fetchTemplates();
    }
  }, [open]);

  // Add effect to filter templates when message type changes
  useEffect(() => {
    if (selectedMessageType) {
      setFilteredTemplates(templates.filter(template => template.type === selectedMessageType));
    } else {
      setFilteredTemplates([]);
    }
  }, [selectedMessageType, templates]);

  const handleOpen = (messageType: MessageType) => {
    setSelectedMessageType(messageType);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedTemplate(null);
    setSubject("");
    setMessage("");
  };

  const handleTemplateChange = (e: SelectChangeEvent<string>) => {
    const templateId = e.target.value;

    if (templateId === "none") {
      setSelectedTemplate(null);
      setSubject("");
      setMessage("");
    } else {
      const template = templates.find((tpl) => tpl._id === templateId) || null;
      setSelectedTemplate(template);

      if (template) {
        if (selectedMessageType === MessageType.EMAIL) {
          setSubject(template.title);
          setMessage(template.message);
        } else if (selectedMessageType === MessageType.PUSH_NOTIFICATION) {
          setSubject(template.title);
          setMessage(template.message.replace(/<[^>]*>/g, ''));
        } else if (selectedMessageType === MessageType.WHATSAPP) {
          setMessage(template.message);
        } else {
          setMessage(template.message.replace(/<[^>]*>/g, ''));
        }
      }
    }
  };
  const handleSendNotification = async () => {
    setLoading(true);
    try {
      if (selectedMessageType === MessageType.SMS) {
        const smsPayload: ManualSMSNotification = {
          phoneNumbers: selectedUsers.map((user) => user.phone),
          message,
        };
        await notificationService.sendSMS(smsPayload);
        setSnackbarMessage("SMS sent successfully!");
        setSnackbarSeverity("success");
      } else if (selectedMessageType === MessageType.EMAIL) {
        const emailPayload: ManualEmailNotification = {
          emails: selectedUsers.map((user) => user.email),
          subject,
          message,
        };
        await notificationService.sendEmail(emailPayload);
        setSnackbarMessage("Email sent successfully!");
        setSnackbarSeverity("success");
      } else if (selectedMessageType === MessageType.PUSH_NOTIFICATION) {
        const pushPayload: ManualPushNotification = {
          userIds: selectedUsers.map((user) => user.email),
          title: subject,
          message,
        };
        await notificationService.sendPush(pushPayload);
        setSnackbarMessage("Push notification sent successfully!");
        setSnackbarSeverity("success");
      } else if (selectedMessageType === MessageType.WHATSAPP) {
        const whatsappPayload: ManualWhatsAppNotification = {
          phoneNumbers: selectedUsers.map((user) => user.phone),
          message,
        };
        await notificationService.sendWhatsApp(whatsappPayload);
        setSnackbarMessage("WhatsApp message sent successfully!");
        setSnackbarSeverity("success");
      }

      setSnackbarOpen(true);
      handleClose();
    } catch (error) {
      console.error("Error sending notification:", error);
      setSnackbarMessage("Failed to send notification. Please try again.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", gap: 2, marginBottom: 2, marginTop: 2, flexWrap: "wrap" }}>
        <Button
          variant="contained"
          color="primary"
          disabled={disabled}
          onClick={() => handleOpen(MessageType.SMS)}
        >
          Send SMS
        </Button>
        <Button
          variant="contained"
          color="secondary"
          disabled={disabled}
          onClick={() => handleOpen(MessageType.EMAIL)}
        >
          Send Email
        </Button>
        <Button
          variant="contained"
          color="success"
          disabled={disabled}
          onClick={() => handleOpen(MessageType.PUSH_NOTIFICATION)}
        >
          Send Push
        </Button>
        <Button
          variant="contained"
          disabled={disabled}
          onClick={() => handleOpen(MessageType.WHATSAPP)}
          sx={{ backgroundColor: "#25D366", "&:hover": { backgroundColor: "#1da851" } }}
        >
          Send WhatsApp
        </Button>
      </Box>

      <Modal open={open} onClose={handleClose}>
        <Box sx={modalStyle}>
          <Paper sx={{ padding: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">
                {selectedMessageType === MessageType.SMS
                  ? "Send SMS"
                  : selectedMessageType === MessageType.EMAIL
                  ? "Send Email"
                  : selectedMessageType === MessageType.WHATSAPP
                  ? "Send WhatsApp"
                  : "Send Push Notification"}{" "}
                to Selected Users
              </Typography>
              <IconButton onClick={handleClose}>
                <CloseIcon />
              </IconButton>
            </Box>

            <Grid container spacing={2} marginTop={1}>
              {/* Template Selection */}
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Template</InputLabel>
                  <Select
                    value={selectedTemplate?._id || "none"}
                    onChange={(e) => handleTemplateChange(e)}
                    label="Select Template"
                  >
                    <MenuItem value="none">None</MenuItem>
                    {filteredTemplates.map((template) => (
                      <MenuItem key={template._id} value={template._id}>
                        {template.title}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Subject (Only for Email or Push Notification) */}
              {(selectedMessageType === MessageType.EMAIL ||
                selectedMessageType === MessageType.PUSH_NOTIFICATION) && (
                <Grid item xs={12}>
                  <TextField
                    label="Subject"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    fullWidth
                  />
                </Grid>
              )}

              {/* Message Box */}
              <Grid item xs={12}>
                <Box>
                  <Typography variant="subtitle1">* Message</Typography>
                  {selectedMessageType === MessageType.EMAIL || selectedMessageType === MessageType.WHATSAPP ? (
                    <Editor
                      value={message}
                      onChange={(e) => {
                        setMessage(e.target.value);
                      }}
                      style={{
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                        padding: "10px",
                        minHeight: "100px",
                      }}
                    >
                      <Toolbar
                        style={{
                          padding: "4px",
                          margin: "0",
                          height: "20px",
                          display: "flex",
                          alignItems: "center",
                          minHeight: "unset",
                          backgroundColor: "#f5f5f5",
                        }}
                      >
                        <BtnUndo />
                        <BtnRedo />
                        <Separator />
                        <BtnBold />
                        <BtnItalic />
                        <BtnStrikeThrough />
                        <Separator />
                        <BtnNumberedList />
                        <BtnBulletList />
                        <Separator />
                        <BtnLink />
                        <BtnClearFormatting />
                        {selectedMessageType === MessageType.EMAIL && (
                          <>
                            <BtnUnderline />
                            <HtmlButton />
                            <Separator />
                            <BtnStyles />
                          </>
                        )}
                      </Toolbar>
                    </Editor>
                  ) : (
                    <TextField
                      multiline
                      rows={4}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      fullWidth
                      placeholder="Enter your message here..."
                      inputProps={{
                        maxLength: selectedMessageType === MessageType.SMS ? 160 : undefined,
                      }}
                      helperText={
                        selectedMessageType === MessageType.SMS
                          ? `${message.length}/160 characters`
                          : undefined
                      }
                      style={{
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                      }}
                    />
                  )}
                </Box>
              </Grid>

              {/* Action buttons */}
              <Grid item xs={12}>
                <Box display="flex" justifyContent="flex-end" gap={2}>
                  <Button variant="text" onClick={handleClose} disabled={loading}>
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSendNotification}
                    disabled={
                      selectedMessageType === MessageType.SMS || selectedMessageType === MessageType.WHATSAPP
                        ? !message || loading
                        : !subject || !message || loading
                    }
                  >
                    {loading ? "Sending..." : "Send"}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      </Modal>

      {/* Add Snackbar component */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// Styling for the modal
const modalStyle = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  height: "100vh",
  overflow: "auto",
};

export default NotificationPopup;
